import React from 'react';
import { format } from 'sql-formatter';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './AppToolSQL.css';

class AppToolSQL extends React.Component {
  constructor(props){
    super(props);
    this.state = {
      raw: '', // 原始SQL
      fmted: '', // 格式化后的SQL
      copy_msg: '',
      error: '',
      usedLanguage: '', // 记录成功使用的语言
      // 格式化配置选项
      config: {
        language: 'auto', // 自动检测或指定语言
        keywordCase: 'upper', // 关键字大小写
        linesBetweenQueries: 2, // 查询间行数
        indent: '  ', // 缩进字符
        tabWidth: 2, // 制表符宽度
        useTabs: false, // 是否使用制表符
        expressionWidth: 50, // 表达式宽度
        denseOperators: false, // 密集操作符
        newlineBeforeSemicolon: false, // 分号前换行
      },
      showConfig: false, // 是否显示配置面板
    }
    this.handleRawChange = this.handleRawChange.bind(this);
    this.handleFormat = this.handleFormat.bind(this);
    this.handleCopy = this.handleCopy.bind(this);
    this.handleConfigChange = this.handleConfigChange.bind(this);
    this.toggleConfig = this.toggleConfig.bind(this);
    this.debounceTimer = null;
  }

  handleRawChange(e) {
    const newValue = e.target.value;
    this.setState({
      raw: newValue,
      error: '',
      copy_msg: '',
      usedLanguage: '',
    });

    // 清除之前的定时器
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // 设置新的定时器，300ms后自动格式化
    this.debounceTimer = setTimeout(() => {
      if (newValue.trim()) {
        this.handleFormat(newValue);
      } else {
        this.setState({ fmted: '', usedLanguage: '' });
      }
    }, 300);
  }

  handleConfigChange(key, value) {
    this.setState(prevState => ({
      config: {
        ...prevState.config,
        [key]: value
      }
    }), () => {
      // 配置改变后重新格式化
      if (this.state.raw.trim()) {
        this.handleFormat(this.state.raw);
      }
    });
  }

  toggleConfig() {
    this.setState(prevState => ({
      showConfig: !prevState.showConfig
    }));
  }

  handleFormat(sql = null) {
    const sqlToFormat = sql || this.state.raw;
    if (!sqlToFormat) {
      this.setState({ fmted: '', error: '', usedLanguage: '' });
      return;
    }

    const { config } = this.state;

    // 如果用户指定了特定语言，直接使用
    if (config.language !== 'auto') {
      try {
        const formattedSql = format(sqlToFormat, {
          language: config.language,
          keywordCase: config.keywordCase,
          linesBetweenQueries: config.linesBetweenQueries,
          indent: config.useTabs ? '\t' : config.indent,
          tabWidth: config.tabWidth,
          expressionWidth: config.expressionWidth,
          denseOperators: config.denseOperators,
          newlineBeforeSemicolon: config.newlineBeforeSemicolon,
        });
        
        this.setState({ 
          fmted: formattedSql, 
          error: '', 
          copy_msg: '',
          usedLanguage: config.language 
        });
        return;
      } catch (error) {
        this.setState({ 
          error: `SQL格式化失败 (${config.language}): ${error.message}`, 
          fmted: sqlToFormat, 
          copy_msg: '',
          usedLanguage: ''
        });
        return;
      }
    }

    // 自动检测模式：尝试所有支持的语言
    const languagesToTry = [
      'mysql', 'postgresql', 'sql', 'sqlite', 'transactsql', 'plsql',
      'bigquery', 'redshift', 'snowflake', 'spark', 'hive', 'trino',
      'mariadb', 'tidb', 'duckdb', 'n1ql', 'db2', 'db2i', 'singlestoredb'
    ];

    // 尝试每种语言进行格式化
    for (const language of languagesToTry) {
      try {
        const formattedSql = format(sqlToFormat, {
          language: language,
          keywordCase: config.keywordCase,
          linesBetweenQueries: config.linesBetweenQueries,
          indent: config.useTabs ? '\t' : config.indent,
          tabWidth: config.tabWidth,
          expressionWidth: config.expressionWidth,
          denseOperators: config.denseOperators,
          newlineBeforeSemicolon: config.newlineBeforeSemicolon,
        });
        
        // 格式化成功
        this.setState({ 
          fmted: formattedSql, 
          error: '', 
          copy_msg: '',
          usedLanguage: language 
        });
        return; // 成功就退出
      } catch (error) {
        // 继续尝试下一个语言
        continue;
      }
    }

    // 所有语言都失败了
    this.setState({ 
      error: 'SQL格式化失败: 尝试了所有支持的SQL方言都无法解析此SQL', 
      fmted: sqlToFormat, 
      copy_msg: '',
      usedLanguage: ''
    });
  }

  handleCopy() {
    if (!this.state.fmted) return;
    try {
      navigator.clipboard.writeText(this.state.fmted).then(() => {
        this.setState({ copy_msg: 'Copy success' });
      }).catch(() => {
        this.setState({ copy_msg: 'Copy failed' });
      });
    } catch (err) {
      this.setState({ copy_msg: 'Copy failed' });
    }
  }

  render() {
    const { raw, fmted, copy_msg, error, usedLanguage, config, showConfig } = this.state;
    
    const supportedLanguages = [
      { value: 'auto', label: '自动检测' },
      { value: 'mysql', label: 'MySQL' },
      { value: 'postgresql', label: 'PostgreSQL' },
      { value: 'sql', label: '标准SQL' },
      { value: 'sqlite', label: 'SQLite' },
      { value: 'transactsql', label: 'Transact-SQL' },
      { value: 'plsql', label: 'PL/SQL' },
      { value: 'bigquery', label: 'BigQuery' },
      { value: 'redshift', label: 'Redshift' },
      { value: 'snowflake', label: 'Snowflake' },
      { value: 'spark', label: 'Spark SQL' },
      { value: 'hive', label: 'Hive' },
      { value: 'trino', label: 'Trino' },
      { value: 'mariadb', label: 'MariaDB' },
      { value: 'tidb', label: 'TiDB' },
      { value: 'duckdb', label: 'DuckDB' },
      { value: 'n1ql', label: 'N1QL' },
      { value: 'db2', label: 'DB2' },
      { value: 'db2i', label: 'DB2i' },
      { value: 'singlestoredb', label: 'SingleStoreDB' },
    ];

    return (
      <div id="tool_sql">
        <div id="content_box">
          {/* 配置面板 */}
          <div className="config-panel">
            <button 
              className="config-toggle" 
              onClick={this.toggleConfig}
            >
              {showConfig ? '隐藏配置' : '显示配置'}
            </button>
            
            {showConfig && (
              <div className="config-options">
                <div className="config-row">
                  <label>SQL方言:</label>
                  <select 
                    value={config.language} 
                    onChange={(e) => this.handleConfigChange('language', e.target.value)}
                  >
                    {supportedLanguages.map(lang => (
                      <option key={lang.value} value={lang.value}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="config-row">
                  <label>关键字大小写:</label>
                  <select 
                    value={config.keywordCase} 
                    onChange={(e) => this.handleConfigChange('keywordCase', e.target.value)}
                  >
                    <option value="upper">大写</option>
                    <option value="lower">小写</option>
                    <option value="preserve">保持原样</option>
                  </select>
                </div>

                <div className="config-row">
                  <label>查询间行数:</label>
                  <input 
                    type="number" 
                    min="0" 
                    max="10"
                    value={config.linesBetweenQueries} 
                    onChange={(e) => this.handleConfigChange('linesBetweenQueries', parseInt(e.target.value))}
                  />
                </div>

                <div className="config-row">
                  <label>缩进样式:</label>
                  <div className="indent-options">
                    <label>
                      <input 
                        type="radio" 
                        name="indentType" 
                        checked={!config.useTabs} 
                        onChange={() => this.handleConfigChange('useTabs', false)}
                      />
                      空格
                    </label>
                    <label>
                      <input 
                        type="radio" 
                        name="indentType" 
                        checked={config.useTabs} 
                        onChange={() => this.handleConfigChange('useTabs', true)}
                      />
                      制表符
                    </label>
                  </div>
                </div>

                <div className="config-row">
                  <label>缩进宽度:</label>
                  <input 
                    type="number" 
                    min="1" 
                    max="8"
                    value={config.tabWidth} 
                    onChange={(e) => this.handleConfigChange('tabWidth', parseInt(e.target.value))}
                  />
                </div>

                <div className="config-row">
                  <label>表达式宽度:</label>
                  <input 
                    type="number" 
                    min="20" 
                    max="200"
                    value={config.expressionWidth} 
                    onChange={(e) => this.handleConfigChange('expressionWidth', parseInt(e.target.value))}
                  />
                </div>

                <div className="config-row">
                  <label>
                    <input 
                      type="checkbox" 
                      checked={config.denseOperators} 
                      onChange={(e) => this.handleConfigChange('denseOperators', e.target.checked)}
                    />
                    密集操作符
                  </label>
                </div>

                <div className="config-row">
                  <label>
                    <input 
                      type="checkbox" 
                      checked={config.newlineBeforeSemicolon} 
                      onChange={(e) => this.handleConfigChange('newlineBeforeSemicolon', e.target.checked)}
                    />
                    分号前换行
                  </label>
                </div>
              </div>
            )}
          </div>

          <textarea
            type="text"
            rows="8"
            placeholder="请输入待格式的SQL。纯前端格式化，数据安全有保障：）"
            cols="130"
            value={raw}
            onChange={this.handleRawChange}
          ></textarea>
          {error && <div style={{color: 'red', marginTop: '8px'}}>{error}</div>}
          {fmted && (
            <div>
              <div id='copybox'>
                <button onClick={this.handleCopy}>Copy</button>
                <span>{copy_msg}</span>
              </div>
              <SyntaxHighlighter
                language="sql"
                style={prism}
                showLineNumbers
                customStyle={{
                  width: '1100px',
                  margin: '10px auto',
                  border: '1px solid #ddd'
                }}
              >
                {fmted}
              </SyntaxHighlighter>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default AppToolSQL;